import { Bill, BillLineItem, BillSortValue } from "@common/types/bill";
import { sortLineItems } from "@common/helpers/lineItems";
import {
    BillLineItemColumn,
    BillLineItemView,
} from "@common/types/views/billViews";
import { View } from "@common/types/views/views";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useCallback, useMemo } from "react";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import classNames from "classnames";
import { createBillLineItemsViewConfig } from "@components/bills/views/billLineItemsView/billLineItemsViewConfig";
import { useAccrualEntityIdsWithLoading } from "@api/ledger.api";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useMultiLedgerColumns } from "@hooks/useMultiLedgerColumns";
import {
    buildLedgerAccountColumnIdByName,
    buildLedgerPayableColumnIdByName,
    injectLedgerColumns,
    isLedgerPayableColumnId,
} from "@lib/ledgerViewColumns";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { PeriodLockDatesProvider } from "@components/periodLock/PeriodLockDatesProvider";
import {
    BillScheduleWiring,
    resolveBillScheduleColumns,
    SCHEDULE_HANDOFF_COLUMN_OPTIONS,
} from "@components/bills/schedules/billScheduleColumns";
import { useBillScheduleTableOptions } from "@components/bills/schedules/useBillScheduleTableOptions";
import { hideAccrualOnlyColumns } from "../billAccrualColumns";
import { useBillMatchTableOptions } from "../useBillMatchTableOptions";
import styles from "../billsView/BillsView.module.scss";

interface Props {
    view: BillLineItemView;
    hasAnyAccrualLedgers: boolean;
    bills: Bill[] | null;
    selectedBillId?: string;
    onBillClick?: (bill: Bill) => void;
    sort?: BillSortValue;
    onSortChange?: (sort: BillSortValue) => void;
    emptyView?: () => React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    selectedLineItems?: Record<string, BillLineItem>;
    hasSelectedByCurrentFilters?: boolean;
    onSelectedLineItemsChange?: (lineItems: BillLineItem[]) => void;
    scheduleWiring?: BillScheduleWiring;
}

const EMPTY_ROW_SELECTION: RowSelectionState = {};

const SCHEDULE_HANDOFF_COLUMN: BillLineItemView["columns"][number] = {
    id: BillLineItemColumn.SCHEDULE,
    options: SCHEDULE_HANDOFF_COLUMN_OPTIONS,
};

export const BillLineItemsView: React.FC<Props> = ({
    view,
    hasAnyAccrualLedgers,
    bills,
    selectedBillId,
    onBillClick,
    sort,
    onSortChange,
    emptyView = () => "No bills found",
    footer,
    className,
    selectedLineItems,
    hasSelectedByCurrentFilters,
    onSelectedLineItemsChange,
    scheduleWiring,
}) => {
    const data = useMemo(
        () =>
            bills
                ?.flatMap((bill) =>
                    bill.lines
                        ? sortLineItems(bill.lines).map((line) => ({
                              ...line,
                              bill,
                          }))
                        : [],
                )
                .filter(Boolean) ?? null,
        [bills],
    ) as BillLineItem[] | null;

    const handleRowClick = useCallback(
        (lineItem?: BillLineItem) => {
            if (lineItem) {
                onBillClick?.(lineItem.bill!);
            }
        },
        [onBillClick],
    );

    const handleFocusedRowChange = useCallback(
        (
            _index: number | null,
            row: BillLineItem | null,
            source: "keyboard" | "click",
        ) => {
            const bill = row?.bill;
            if (
                source !== "keyboard" ||
                selectedBillId === undefined ||
                !bill ||
                bill.id === selectedBillId
            ) {
                return;
            }
            onBillClick?.(bill);
        },
        [selectedBillId, onBillClick],
    );

    const checkSiblings = useCallback(
        (invoiceLineItem: BillLineItem) => {
            const index = data?.findIndex((i) => i.id === invoiceLineItem.id);

            if (index === undefined || !data) {
                return {
                    hasTopSibling: false,
                    hasBottomSibling: false,
                };
            }

            return {
                hasTopSibling:
                    index > 0 &&
                    data[index - 1].billId === invoiceLineItem.billId,
                hasBottomSibling:
                    index < data.length - 1 &&
                    data[index + 1].billId === invoiceLineItem.billId,
            };
        },
        [data],
    );

    const entityIds = useMemo(
        () => bills?.map((bill) => bill.entityId) ?? [],
        [bills],
    );
    const { accrualEntityIds, isLoading: isLoadingAccrualLedgers } =
        useAccrualEntityIdsWithLoading(entityIds);
    const multiLedgerLedgers = useMultiLedgerColumns(entityIds, {
        basis: AccountingLedgerBasis.accruals,
        uniqueByBasisAndName: true,
    });

    const scheduleOptions = useBillScheduleTableOptions(scheduleWiring);
    const scheduleCallbacks = scheduleWiring?.callbacks;
    const billIds = useMemo(() => bills?.map((bill) => bill.id) ?? [], [bills]);
    const isMatchColumnVisible =
        hasAnyAccrualLedgers &&
        view.columns.some((column) => column.id === BillLineItemColumn.MATCH);
    const matchOptions = useBillMatchTableOptions({
        billIds,
        isMatchColumnVisible,
    });
    const isReadonly = view.options?.readonly;
    const tableMeta = useMemo(
        () => ({
            callbacks: {
                onBillClick,
                checkSiblings,
                ...scheduleCallbacks,
            },
            options: {
                ...scheduleOptions,
                ...matchOptions,
                readonly: isReadonly,
            },
            accrualEntityIds,
            isLoadingAccrualLedgers,
        }),
        [
            onBillClick,
            checkSiblings,
            scheduleCallbacks,
            scheduleOptions,
            matchOptions,
            isReadonly,
            accrualEntityIds,
            isLoadingAccrualLedgers,
        ],
    );

    const rowSelection = useMemo(() => {
        if (!view.options?.bulkActions) {
            return EMPTY_ROW_SELECTION;
        }

        const selection: RowSelectionState = {};

        if (hasSelectedByCurrentFilters) {
            data?.forEach((lineItem) => {
                selection[lineItem.id] = true;
            });
        } else if (selectedLineItems) {
            Object.keys(selectedLineItems).forEach((id) => {
                selection[id] = true;
            });
        }

        return Object.keys(selection).length > 0
            ? selection
            : EMPTY_ROW_SELECTION;
    }, [
        data,
        hasSelectedByCurrentFilters,
        selectedLineItems,
        view.options?.bulkActions,
    ]);

    const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) => {
            if (!data || !onSelectedLineItemsChange) {
                return;
            }

            const nextSelection =
                typeof updater === "function" ? updater(rowSelection) : updater;
            const selectedIds = Object.keys(nextSelection).filter(
                (id) => nextSelection[id],
            );

            const selected = data.filter((lineItem) =>
                selectedIds.includes(lineItem.id),
            );
            onSelectedLineItemsChange(selected);
        },
        [data, onSelectedLineItemsChange, rowSelection],
    );

    const enableRowSelection = useMemo(() => {
        if (!view.options?.bulkActions) {
            return false;
        }
        if (hasSelectedByCurrentFilters) {
            return false;
        }

        return true;
    }, [hasSelectedByCurrentFilters, view.options?.bulkActions]);

    const isClassesEnabled = useIsClassesEnabled();
    const isSchedulesEnabled = scheduleWiring != null;
    const billLineItemsViewConfig = useMemo(
        () =>
            createBillLineItemsViewConfig({
                hasAnyAccrualLedgers,
                isClassesEnabled,
                isSchedulesEnabled,
                multiLedgerLedgers,
            }),
        [
            hasAnyAccrualLedgers,
            isClassesEnabled,
            isSchedulesEnabled,
            multiLedgerLedgers,
        ],
    );
    const visibleView = useMemo(
        () =>
            injectLedgerColumns({
                view: {
                    ...view,
                    columns: hideAccrualOnlyColumns({
                        columns: resolveBillScheduleColumns({
                            columns: view.columns,
                            handoffColumn: SCHEDULE_HANDOFF_COLUMN,
                            amountColumnId: BillLineItemColumn.AMOUNT,
                            wiring: scheduleWiring,
                        }),
                        hasAnyAccrualLedgers,
                    }).filter((column) => {
                        const shouldHidePayableColumn =
                            !hasAnyAccrualLedgers &&
                            (column.id ===
                                BillLineItemColumn.ACCOUNTS_PAYABLE ||
                                isLedgerPayableColumnId(column.id));
                        return !shouldHidePayableColumn;
                    }),
                },
                ledgers: multiLedgerLedgers,
                replaceColumnIds: [
                    BillLineItemColumn.ACCRUAL_ACCOUNT,
                    BillLineItemColumn.ACCOUNTS_PAYABLE,
                ],
                buildColumnIds: (ledger) => [
                    buildLedgerAccountColumnIdByName(ledger),
                    buildLedgerPayableColumnIdByName(ledger),
                ],
            }),
        [hasAnyAccrualLedgers, view, multiLedgerLedgers, scheduleWiring],
    );

    return (
        <PeriodLockDatesProvider entityIds={entityIds}>
            <ViewTable
                className={classNames(styles.billsView, className)}
                data={data}
                view={visibleView as View}
                config={billLineItemsViewConfig}
                selectedRowId={selectedBillId}
                onRowClick={handleRowClick}
                onFocusedRowChange={handleFocusedRowChange}
                sort={sort}
                onSortChange={onSortChange}
                emptyView={emptyView}
                tableMeta={tableMeta}
                footer={footer}
                fixedTopOffsetPx
                rowSelection={rowSelection}
                onRowSelectionChange={handleRowSelectionChange}
                enableRowSelection={enableRowSelection}
                enableKeyboardShortcuts
            />
        </PeriodLockDatesProvider>
    );
};
